import { RegistrantResponse } from '@/connection/audiences/registrants';
import {
  PASSCODE_LIST_MEMBER_ORDER_OPTIONS,
  PasscodeListMemberSort,
  REGISTERED_USER_ORDER_OPTIONS,
  Sort,
  UpdateCompletedState,
} from './types';
import { ManuallyAddedUser, WorkingPasscodeList } from '@/types/passcode-list.types';
import {
  DownloadCSVStringValues,
  DownloadStringValues,
  PASSCODE_LIST_TYPE,
  PasscodeList,
  PasscodeListMember,
  RegFieldsEnum,
} from '@/types/registration.types';
import { GetMagicLinksListTemplate } from '@/connection/audiences/passcode-lists';

const SortToOrderOptionsMap: Map<string, REGISTERED_USER_ORDER_OPTIONS> = new Map([
  ['', REGISTERED_USER_ORDER_OPTIONS.UNORDERED],
  ['name|asc', REGISTERED_USER_ORDER_OPTIONS.NAME_ASC],
  ['name|desc', REGISTERED_USER_ORDER_OPTIONS.NAME_DESC],
  ['email|asc', REGISTERED_USER_ORDER_OPTIONS.EMAIL_ASC],
  ['email|desc', REGISTERED_USER_ORDER_OPTIONS.EMAIL_DESC],
  ['locations|asc', REGISTERED_USER_ORDER_OPTIONS.LOCATION_ASC],
  ['locations|desc', REGISTERED_USER_ORDER_OPTIONS.LOCATION_DESC],
  ['created|asc', REGISTERED_USER_ORDER_OPTIONS.DATE_ASC],
  ['created|desc', REGISTERED_USER_ORDER_OPTIONS.DATE_DESC],
  ['marked_attended_by_admin|asc', REGISTERED_USER_ORDER_OPTIONS.ATTENDED_ASC],
  ['marked_attended_by_admin|desc', REGISTERED_USER_ORDER_OPTIONS.ATTENDED_DESC],
  ['last_login|asc', REGISTERED_USER_ORDER_OPTIONS.LAST_LOGIN_ASC],
  ['last_login|desc', REGISTERED_USER_ORDER_OPTIONS.LAST_LOGIN_DESC],
  ['course_completion|asc', REGISTERED_USER_ORDER_OPTIONS.COURSE_COMPLETION_ASC],
  ['course_completion|desc', REGISTERED_USER_ORDER_OPTIONS.COURSE_COMPLETION_DESC],
  ['reg_status|asc', REGISTERED_USER_ORDER_OPTIONS.REGISTRATION_STATUS_ASC],
  ['reg_status|desc', REGISTERED_USER_ORDER_OPTIONS.REGISTRATION_STATUS_DESC],
  ['star_score|asc', REGISTERED_USER_ORDER_OPTIONS.STAR_SCORE_ASC],
  ['star_score|desc', REGISTERED_USER_ORDER_OPTIONS.STAR_SCORE_DESC],
  ['watchtime|asc', REGISTERED_USER_ORDER_OPTIONS.WATCHTIME_ASC],
  ['watchtime|desc', REGISTERED_USER_ORDER_OPTIONS.WATCHTIME_DESC],
]);

export const OptionsEnumToSortOrder = (order: REGISTERED_USER_ORDER_OPTIONS): Sort => {
  const reversedOptions = new Map([...SortToOrderOptionsMap].map(([key, value]) => [value, key]));
  const key = reversedOptions.get(order);
  const [sortBy, sortOrder] = key?.split('|') || ['created', 'desc'];
  return { sortBy: sortBy as Sort['sortBy'], sortOrder: sortOrder as Sort['sortOrder'] };
};

export const SortToOrderOptionsEnum = (sort: Sort): `${REGISTERED_USER_ORDER_OPTIONS}` => {
  const key = `${sort.sortBy}|${sort.sortOrder}`;
  const order = SortToOrderOptionsMap.get(key);
  return `${order || REGISTERED_USER_ORDER_OPTIONS.DATE_DESC}`;
};

export const getCourseCompletedState = (
  profile: RegistrantResponse,
  updatingUsers?: number[],
  generatingPdfForUsers?: number[]
) => {
  if (updatingUsers?.includes(profile.bl_profile)) {
    return UpdateCompletedState.Updating;
  }

  if (profile.generating_pdf || generatingPdfForUsers?.includes(profile.bl_profile)) {
    return UpdateCompletedState.GeneratingCertificate;
  }

  if (profile.pdf_url) {
    return UpdateCompletedState.CertificateAvailable;
  }

  if (profile.course_completed) {
    return UpdateCompletedState.Passed;
  }

  if (profile.course_completed === false) {
    return UpdateCompletedState.NotPassed;
  }

  return UpdateCompletedState.None;
};

export const isValidEmail = (email: string): boolean => {
  const re =
    //eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const allListMembersValid = (workingPasscodeList: WorkingPasscodeList): boolean => {
  let valid = true;
  const passcodeListType = workingPasscodeList.type;
  const partiallyFilledManualUsers = workingPasscodeList.manually_added_users.filter((user) => anyFieldHasValue(user));

  //if we don't have an upload url AND we don't have any partially filled out manually added users, dont allow save
  if (!workingPasscodeList.url.length && !partiallyFilledManualUsers.length) {
    return false;
  }

  if (partiallyFilledManualUsers.length) {
    if ([PASSCODE_LIST_TYPE.PASSCODES_ONLY, PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES].includes(passcodeListType)) {
      //if passcode is required, all users should have a passcode
      valid = partiallyFilledManualUsers.every((user) => {
        return user.passcode.length > 0;
      });
      if (!valid) {
        return valid;
      }
    }

    if (
      [
        PASSCODE_LIST_TYPE.EMAILS_ONLY,
        PASSCODE_LIST_TYPE.MAGIC_LINKS,
        PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION,
        PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
      ].includes(workingPasscodeList.type)
    ) {
      //if email is required, all users should have a valid email
      valid = partiallyFilledManualUsers.every((user) => {
        return isValidEmail(user.email);
      });

      if (!valid) {
        return valid;
      }
    }
  }

  return valid;
};

export const anyFieldHasValue = (user: ManuallyAddedUser): boolean => {
  if (user) {
    return Object.values(user).some((value) => value.length > 0);
  }

  return false;
};

export const getDefaultPasscodeList = (
  projectUuid: string | undefined,
  isMagicLinkRegistration: boolean = false
): WorkingPasscodeList => {
  return {
    type: isMagicLinkRegistration ? PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION : PASSCODE_LIST_TYPE.EMAILS_ONLY,
    name: isMagicLinkRegistration ? `${projectUuid} - Magic Link Registration` : '',
    max_uses: 0,
    master_passcode: '',
    url: '',
    manually_added_users: [{ email: '', first_name: '', last_name: '', passcode: '' }] as ManuallyAddedUser[],
  };
};

export const mapToWorkingPasscodeList = (
  projectUuid: string | undefined,
  passcodeList: PasscodeList | null,
  isMagicLinkRegistration: boolean = false
): WorkingPasscodeList => {
  return passcodeList
    ? {
        type: passcodeList.type,
        name: passcodeList.name,
        max_uses: passcodeList.max_uses,
        master_passcode: passcodeList.master_passcode || null,
        url: '',
        manually_added_users: [{ email: '', first_name: '', last_name: '', passcode: '' }] as ManuallyAddedUser[],
      }
    : getDefaultPasscodeList(projectUuid, isMagicLinkRegistration);
};

const PasscodeListMemberSortToOrderOptionsMap: Map<string, PASSCODE_LIST_MEMBER_ORDER_OPTIONS> = new Map([
  ['', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.UNORDERED],
  ['email|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.EMAIL_ASC],
  ['email|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.EMAIL_DESC],
  ['passcode|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.PASSCODE_ASC],
  ['passcode|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.PASSCODE_DESC],
  ['uses|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.USES_ASC],
  ['uses|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.USES_DESC],
  ['status|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.STATUS_ASC],
  ['status|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.STATUS_DESC],
  ['events|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.EVENTS_ASC],
  ['events|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.EVENTS_DESC],
  ['first_name|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.FIRST_NAME_ASC],
  ['first_name|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.FIRST_NAME_DESC],
  ['last_name|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.LAST_NAME_ASC],
  ['last_name|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.LAST_NAME_DESC],
  ['fullname|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.FULL_NAME_ASC],
  ['fullname|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.FULL_NAME_DESC],
  ['magic_link_invite_sent|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.MAGIC_LINK_INVITE_SENT_ASC],
  ['magic_link_invite_sent|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.MAGIC_LINK_INVITE_SENT_DESC],
  ['created|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.CREATED_ASC],
  ['created|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.CREATED_DESC],
  ['last_login|asc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.LAST_LOGIN_ASC],
  ['last_login|desc', PASSCODE_LIST_MEMBER_ORDER_OPTIONS.LAST_LOGIN_DESC],
]);

export const PasscodeListMemberOptionsEnumToSortOrder = (
  order: PASSCODE_LIST_MEMBER_ORDER_OPTIONS
): PasscodeListMemberSort => {
  const reversedOptions = new Map([...PasscodeListMemberSortToOrderOptionsMap].map(([key, value]) => [value, key]));
  const key = reversedOptions.get(order);
  const [sortBy, sortOrder] = key?.split('|') || ['created', 'desc'];
  return {
    sortBy: sortBy as PasscodeListMemberSort['sortBy'],
    sortOrder: sortOrder as PasscodeListMemberSort['sortOrder'],
  };
};

export const PasscodeListMemberSortToOrderOptionsEnum = (
  sort: PasscodeListMemberSort
): `${PASSCODE_LIST_MEMBER_ORDER_OPTIONS}` => {
  const key = `${sort.sortBy}|${sort.sortOrder}`;
  const order = PasscodeListMemberSortToOrderOptionsMap.get(key);
  return `${order || PASSCODE_LIST_MEMBER_ORDER_OPTIONS.UNORDERED}`;
};

export const PasscodeListTypeTableColumns = {
  [PASSCODE_LIST_TYPE.EMAILS_ONLY]: ['first_name', 'last_name', 'email', 'uses'],
  [PASSCODE_LIST_TYPE.PASSCODES_ONLY]: ['first_name', 'last_name', 'passcode', 'uses'],
  [PASSCODE_LIST_TYPE.MAGIC_LINKS]: ['first_name', 'last_name', 'email', 'magic_links_download_link'],
  [PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION]: [
    'first_name',
    'last_name',
    'email',
    'magic_links_download_link',
    'magic_link_invite_sent',
  ],
  [PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES]: ['first_name', 'last_name', 'email', 'passcode', 'uses'],
};

export async function GenerateExampleCsv(type: PASSCODE_LIST_TYPE, event_uuid?: string): Promise<void> {
  let csv = '';

  switch (type) {
    case PASSCODE_LIST_TYPE.EMAILS_ONLY: {
      csv += 'email*,first_name,last_name\n';
      csv += 'user1@example.com,First 1, Last 1\nuser2@example.com,First 2,Last 2';
      break;
    }
    case PASSCODE_LIST_TYPE.PASSCODES_ONLY: {
      csv += 'passcode*\n';
      csv += 'first-passcode\nsecond-passcode';
      break;
    }
    case PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES: {
      csv += 'email*,passcode*,first_name,last_name\n';
      csv += 'user1@example.com,my-first-passcode,First 1,First 2\nuser2@example.com,my-second-passcode,Last 1,Last 2';
      break;
    }
    case PASSCODE_LIST_TYPE.MAGIC_LINKS: {
      if (event_uuid) {
        const { csv: incomingCSV } = await GetMagicLinksListTemplate(event_uuid);
        csv = incomingCSV;
      }
      break;
    }
    case PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION: {
      csv += 'email address*,first name,last name\n';
      csv += 'user1@example.com,First 1,Last 1\nuser2@example.com,First 2,Last 2';
      break;
    }
    default: {
      csv += 'email,passcode,first_name,last_name\n';
      csv += 'user1@example.com,my-first-passcode,First 1,First 2\nuser2@example.com,my-second-passcode,Last 1,Last 2';
    }
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', 'audience-example.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function normalExport(list: PasscodeList) {
  return `/v3/admin/content/passcode-lists/export/${list.passcode_list}/${list.type}`;
}
function specificExport(list: PasscodeList) {
  return `/v3/admin/content/passcode-lists/export-specific/${list.passcode_list}/${list.type}`;
}

export const getExportUrl = (list: PasscodeList, downloadType: DownloadStringValues): string => {
  let exportURL = '';

  switch (downloadType) {
    case DownloadCSVStringValues.NORMAL:
      exportURL = normalExport(list);
      break;
    case DownloadCSVStringValues.SPECIFIC:
      exportURL = specificExport(list);
      break;
    case DownloadCSVStringValues.SPECIFIC_MAGIC:
      exportURL = specificExport(list);
      break;
    case DownloadCSVStringValues.NORMAL_MAGIC:
      exportURL = specificExport(list);
      break;
    default:
      exportURL = normalExport(list);
      break;
  }

  return exportURL;
};

export const getExportBody = (
  list: PasscodeList,
  downloadType: DownloadStringValues,
  selectedMembers?: PasscodeListMember[],
  projectUuid: string | null = null,
  dmaID?: string | null
) => {
  const EXPORT_BODIES = {
    NORMAL: { downloadString: downloadType },
    SPECIFIC: { downloadString: downloadType, members: selectedMembers, channel: list.channel },
    SPECIFIC_MAGIC: { downloadString: downloadType, members: selectedMembers, channel: list.channel },
    NORMAL_MAGIC: { downloadString: downloadType, channel: list.channel, event_uuid: projectUuid },
  };

  return { dmaID, ...EXPORT_BODIES[downloadType] };
};

export const getExportDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const downloadCsv = async (res: any, fileName: string) => {
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = `${fileName}.csv`.trim();
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

export const formatDateReadableLocale = (date: Date) => {
  const month = date.toLocaleString('default', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
};

export const formatTimeReadableLocale = (date: Date) => {
  const time = date.toLocaleTimeString('default', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  return time.toUpperCase();
};

export const listMemberName = (member: PasscodeListMember) => {
  const firstName = member?.imported_profile?.[RegFieldsEnum.first_name];
  const lastName = member?.imported_profile?.[RegFieldsEnum.last_name];
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName || 'Name not set';
  }
};

export const registrantName = (profile: RegistrantResponse) => {
  const firstName = profile.first_name;
  const lastName = profile.last_name;
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName || 'Name not set';
  }
};
